import http from '../http'

/**
 * 查询采购订单
 * @param {Object} params - 查询参数
 * @param {string} [params.orderTitle] - 订单标题（模糊查询）
 * @param {string} [params.orderId] - 订单编号（模糊查询）
 * @param {string} [params.status] - 状态
 * @param {string} [params.orderType] - 订单类型（如"01"表示正常订单，"02"表示退货订单）
 * @param {number} [params.page=0] - 页码，默认为0
 * @param {number} [params.size=10] - 每页数量，默认为10
 * @returns {Promise<Object>} 采购订单列表及分页信息
 */
export function query_purchase_orders(params = {}) {
  // 处理分页参数，后端使用page/size格式
  const queryParams = {
    orderTitle: params.orderTitle,
    orderId: params.orderId,
    status: params.status,
    orderType: params.orderType, // 添加订单类型参数
    page: params.page || 0,
    size: params.size || 10
  };
  
  // 移除undefined值
  Object.keys(queryParams).forEach(key => {
    if (queryParams[key] === undefined) {
      delete queryParams[key];
    }
  });
  
return http.get('/orders/purchase', { params: queryParams });
}

/**
 * 查询销售订单
 * @param {Object} params - 查询参数
 * @param {string} [params.orderTitle] - 订单标题（模糊查询）
 * @param {string} [params.orderId] - 订单编号（模糊查询）
 * @param {string} [params.status] - 状态
 * @param {string} [params.orderType] - 订单类型（如"01"表示正常订单，"02"表示退货订单）
 * @param {number} [params.page=0] - 页码，默认为0
 * @param {number} [params.size=10] - 每页数量，默认为10
 * @returns {Promise<Object>} 销售订单列表及分页信息
 */
export function query_sales_orders(params = {}) {
  // 处理分页参数，后端使用page/size格式
  const queryParams = {
    orderTitle: params.orderTitle,
    orderId: params.orderId,
    status: params.status,
    orderType: params.orderType, // 添加订单类型参数
    page: params.page || 0,
    size: params.size || 10
  };
  
  // 移除undefined值
  Object.keys(queryParams).forEach(key => {
    if (queryParams[key] === undefined) {
      delete queryParams[key];
    }
  });
  
return http.get('/orders/sales', { params: queryParams });
}

/**
 * 分页查询订单信息（旧接口，保持兼容性）
 * @param {Object} params - 查询参数
 * @param {string} [params.contract_name] - 合同名称（模糊查询）
 * @param {number} [params.create_id] - 创建者ID
 * @param {number} [params.contract_id] - 合同ID
 * @param {number} [params.supplier_id] - 供应商ID
 * @param {string} [params.status] - 订单状态
 * @param {number} [params.page=1] - 页码
 * @param {number} [params.pageSize=10] - 每页记录数
 * @returns {Promise<Object>} 订单列表及分页信息
 */
export function query_orders_pagination(params = {}) {
  // 处理分页参数，确保参数名称与后端一致
  const queryParams = { ...params };
  
  // 设置默认分页参数
  if (!queryParams.page) {
    queryParams.page = 1;
  }
  
  if (!queryParams.pageSize) {
    queryParams.pageSize = 10;
  }
  
  // 移除不被后端接受的参数
  const validKeys = ['contract_name', 'create_id', 'contract_id', 'supplier_id', 'status', 'page', 'pageSize'];
  Object.keys(queryParams).forEach(key => {
    if (!validKeys.includes(key)) {
      delete queryParams[key];
    }
  });
  
return http.get('/orders', { params: queryParams });
}

/**
 * 获取订单详情
 * @param {Number} id - 订单ID
 * @returns {Promise} - 返回订单详情的Promise对象，包括订单基本信息和订单明细
 */
export function get_order_detail(id) {
return http.get(`/orders/${id}`)
}

/**
 * 根据多个订单ID查询订单列表
 * @param {Array} ids - 订单ID数组
 * @returns {Promise} - 返回订单列表的Promise对象
 */
export function get_orders_by_ids(ids) {
  if (!ids || !Array.isArray(ids) || ids.length === 0) {
    return Promise.reject(new Error('订单ID数组不能为空'))
  }
  
  const idsParam = ids.join(',')
return http.get('/orders/by-ids', { params: { ids: idsParam } })
}

/**
 * 保存订单信息（新增或更新）
 * @param {Object} data - 订单数据
 * @param {Number} [data.id] - 订单ID(更新时提供，新增时不提供)
 * @param {String} data.orderTitle - 订单标题，必填
 * @param {Number} data.contractId - 合同ID，必填
 * @param {String} data.contractType - 合同类型(SALES/PURCHASE)，必填
 * @param {String} data.orderType - 订单类型(01直采/02集采)，必填
 * @param {Number} data.supplierId - 供应商ID，必填
 * @param {String} data.contactPerson - 联系人姓名，必填
 * @param {String} data.contactPhone - 联系电话，必填
 * @param {String} data.contactAddress - 联系地址，必填
 * @param {Number} data.productsTotal - 产品总数量，必填
 * @param {Number} data.priceTotal - 总价，必填
 * @param {Array} data.orderItems - 订单明细列表，必填
 * @returns {Promise} - 返回保存结果的Promise对象
 */
export function create_order(data) {
return http.post('/orders', data)
}

/**
 * 更新订单状态
 * @param {Number} id - 订单ID
 * @param {String} status - 订单新状态
 * @returns {Promise} - 返回更新结果的Promise对象
 */
export function update_order_status(id, status) {
return http.put(`/orders/${id}/status`, null, { params: { status } })
}

/**
 * 分发订单到供应商（创建子订单）
 * @param {Number} parentId - 父订单ID
 * @param {Array} childOrders - 子订单数据数组
 * @returns {Promise} - 返回分发结果的Promise对象
 */
export function distribute_order(parentId, childOrders) {
// 使用Promise.all并行处理所有子订单的创建请求
  const createOrderPromises = childOrders.map(orderData => {
    // 确保订单包含父订单ID
    const childOrderData = {
      ...orderData,
      parent_order_id: parentId
    };
    
// 调用已有的创建订单接口
    return create_order(childOrderData);
  });
  
  // 等待所有创建订单的请求完成
  return Promise.all(createOrderPromises)
    .then(responses => {
// 提取所有创建的子订单ID
      const childOrderIds = responses.map(response => {
        if (response.data && response.data.success && response.data.data) {
          // 如果响应中包含订单ID信息，则使用该ID
          return response.data.data.id || response.data.data.order_id || null;
        }
        return null;
      }).filter(id => id !== null);
      
      // 更新父订单状态为"已分发"
      return update_order_status(parentId, '已分发')
        .then(() => {
          // 创建一个成功的响应
          return {
            data: {
              success: true,
              message: `成功创建 ${responses.length} 个子订单，并更新父订单状态为"已分发"`,
              data: childOrderIds
            }
          };
        })
        .catch(statusError => {
          console.error('更新父订单状态失败:', statusError);
          
          // 即使状态更新失败，仍然返回子订单创建成功
          return {
            data: {
              success: true,
              message: `成功创建 ${responses.length} 个子订单，但更新父订单状态失败`,
              data: childOrderIds
            }
          };
        });
    })
    .catch(error => {
      // 处理错误情况
      console.error('创建子订单失败:', error);
      
      // 返回一个错误响应
      return {
        data: {
          success: false,
          message: '创建子订单失败: ' + (error.message || '未知错误'),
          data: []
        }
      };
    });
}

/**
 * 取消订单分发（逻辑：删除所有子订单，并将父订单状态恢复为"已创建"）
 * @param {Number} parentId - 父订单ID
 * @returns {Promise} - 返回操作结果的Promise对象
 */
export function cancel_distribution(parentId) {
// 步骤1: 获取所有子订单
  return http.get(`/orders`, { params: { parent_order_id: parentId } })
    .then(response => {
      if (!response.data || !response.data.success) {
        throw new Error('获取子订单失败: ' + (response.data?.message || '未知错误'));
      }
      
      const childOrders = response.data.data.list || [];
// 检查子订单状态
      const nonPendingOrders = childOrders.filter(order => order.status !== '待接受');
      if (nonPendingOrders.length > 0) {
        throw new Error(`有 ${nonPendingOrders.length} 个子订单不是"待接受"状态，无法取消分发`);
      }
      
      // 步骤2: 删除所有子订单
      const deletePromises = childOrders.map(order => delete_order(order.order_id));
      return Promise.all(deletePromises)
        .then(() => {
// 步骤3: 将父订单状态恢复为"已创建"
          return update_order_status(parentId, '已创建')
            .then(() => {
              return {
                data: {
                  success: true,
                  message: `成功取消分发，删除了 ${childOrders.length} 个子订单`,
                  data: null
                }
              };
            });
        });
    })
    .catch(error => {
      console.error('取消订单分发失败:', error);
      return {
        data: {
          success: false,
          message: '取消订单分发失败: ' + (error.message || '未知错误'),
          data: null
        }
      };
    });
}

/**
 * 删除订单（逻辑删除）
 * @param {Number} id - 订单ID
 * @returns {Promise} - 返回删除结果的Promise对象
 */
export function delete_order(id) {
return http.delete(`/orders/${id}`)
}

/**
 * 创建随机测试订单
 * @returns {Promise} - 返回创建结果的Promise对象
 */
export async function create_random_test_order() {
  try {
// 随机生成订单项数据
    const orderItems = [
      {
        category: "钢材",
        materialsName: `螺纹钢_${Math.floor(Math.random() * 100)}`,
        materialsSpec: "Φ16",
        unit: "吨",
        quantity: Math.floor(Math.random() * 10) + 1,
        beforePrice: 4000,
        taxRate: 0.13,
        price: 4520,
        productSupplierId: 1
      },
      {
        category: "水泥",
        materialsName: `通用水泥_${Math.floor(Math.random() * 100)}`,
        materialsSpec: "P.O42.5",
        unit: "吨",
        quantity: Math.floor(Math.random() * 20) + 5,
        beforePrice: 500,
        taxRate: 0.13,
        price: 565,
        productSupplierId: 2
      }
    ];
    
    // 计算总数量和总价
    const productsTotal = orderItems.reduce((sum, item) => sum + item.quantity, 0);
    const priceTotal = orderItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    // 按照新API格式生成订单数据
    const randomData = {
      orderTitle: `测试订单_${new Date().getTime()}`,
      contractId: Math.floor(Math.random() * 10) + 1,
      contractType: "SALES",
      orderType: "01",
      supplierId: 1,
      contactPerson: `测试联系人_${Math.floor(Math.random() * 100)}`,
      contactPhone: `138${Math.floor(Math.random() * 100000000).toString().padStart(8, '0')}`,
      contactAddress: `测试地址_${Math.floor(Math.random() * 1000)}号`,
      productsTotal: productsTotal,
      priceTotal: priceTotal,
      orderItems: orderItems
    }
    
// 调用创建订单API
    const response = await create_order(randomData)
return response
  } catch (error) {
    console.error('创建随机测试订单失败:', error)
    return Promise.reject({
      success: false,
      message: '创建随机测试订单失败: ' + (error.message || '未知错误'),
      error: error.toString()
    })
  }
}

/**
 * 获取可退货的订单列表
 * @returns {Promise} - 返回可退货订单列表的Promise对象
 */
export function get_returnable_orders() {
return http.get('/return-orders/returnable')
}

/**
 * 创建退货订单
 * @param {Object} data - 退货订单数据
 * @param {Number} data.originalOrderId - 原始订单ID（必填）
 * @param {String} data.returnReason - 退货原因（必填，最大500字符）
 * @param {String} data.orderTitle - 退货订单标题（必填，最大200字符）
 * @param {Array} data.returnItems - 退货明细列表（必填，至少包含一个退货明细）
 * @returns {Promise} - 返回创建结果的Promise对象
 */
export function create_return_order(data) {
return http.post('/return-orders', data)
}

/**
 * 根据原始订单ID获取退货订单列表
 * @param {Number} originalOrderId - 原始订单ID
 * @param {Object} params - 查询参数
 * @param {Number} [params.page=0] - 页码，默认为0
 * @param {Number} [params.size=10] - 每页数量，默认为10
 * @returns {Promise} - 返回退货订单列表的Promise对象
 */
export function get_return_orders_by_original_order(originalOrderId, params = {}) {
  const queryParams = {
    page: params.page || 0,
    size: params.size || 10
  }
  
return http.get(`/return-orders/original/${originalOrderId}`, { params: queryParams })
}

/**
 * 获取退货订单详情
 * @param {Number} returnOrderId - 退货订单ID
 * @returns {Promise} - 返回退货订单详情的Promise对象
 */
export function get_return_order_detail(returnOrderId) {
return http.get(`/return-orders/${returnOrderId}`)
}

export function confirm_return_order(returnOrderId) {
return http.post(`/return-orders/${returnOrderId}/confirm`)
}

export default {
  query_orders_pagination,
  query_purchase_orders,
  query_sales_orders,
  get_order_detail,
  create_order,
  update_order_status,
  distribute_order,
  delete_order,
  create_random_test_order,
  cancel_distribution,
  // 退货订单相关
  get_returnable_orders,
  create_return_order,
  get_return_orders_by_original_order,
  get_return_order_detail,
  confirm_return_order
}